import { useEffect, useRef, useState } from 'react';
import type { MutableRefObject } from 'react';
import { ApiHttpError, getErrorMessage } from '@/api';
import type { Level } from '@/api/types';
import { APP_STAGES, changeStageGrants, getMemberStageGrants } from '@/api/stageGrants';
import type {
  AppStage,
  StageGrant,
  StageGrantChange,
} from '@/api/stageGrants';
import { useToast } from '@/components/ui';
import { createStageGrantRequest } from '@/api/requests';
import { useFetch } from '@/hooks/useFetch';
import StageGrantsPanel, { stageLabel } from './StageGrantsPanel';
import type { StageDraft, StageGrantsPanelState } from './StageGrantsPanel';
import { readStageOperations, storeStageOperations } from './stageOperations';
import type { StageOperation } from './stageOperations';

type Draft = StageDraft;

export interface StageGrantActionHandle {
  // Stage 변경 저장. 성공(또는 저장할 변경 없음)이면 true, 실패면 false.
  submit: () => Promise<boolean>;
  // 비admin: 변경을 즉시 저장하지 않고 요청함에 올린다(승인 후 반영). 성공하면 선택을 저장된 상태로 되돌린다.
  request: (reason: string) => Promise<boolean>;
  setStage: (stage: AppStage, level: Level | null) => void;
  toggleStage: (stage: AppStage) => void;
  reload: () => void;
}

export interface StageGrantActionState extends StageGrantsPanelState {
  heldStages: AppStage[];
  availableStages: AppStage[];
  changeCount: number;
  applying: boolean;
  disabled: boolean;
  buttonLabel: string;
  summary: string;
}

export interface StageGrantSummary {
  stage: AppStage;
  level: Level;
  changeKind: 'grant' | 'update' | 'revoke' | 'current';
}

const grantMap = (grants: StageGrant[]): Draft =>
  Object.fromEntries(grants.map(grant => [grant.stage, grant.level])) as Draft;

const applyChanges = (base: Draft, changes: StageGrantChange[]): Draft => {
  const next = { ...base };
  for (const change of changes) {
    if (change.level === null) delete next[change.stage];
    else next[change.stage] = change.level;
  }
  return next;
};

export default function StageGrantsCard({
  email,
  onSaved,
  onBusyChange,
  existingGrantWarning,
  lockedMessage,
  disabled = false,
  collapsible = false,
  actionRef,
  onActionStateChange,
  onSummaryChange,
}: {
  email: string | null;
  onSaved?: () => void;
  existingGrantWarning?: string;
  lockedMessage?: string;
  disabled?: boolean;
  collapsible?: boolean;
  onBusyChange?: (busy: boolean) => void;
  actionRef?: MutableRefObject<StageGrantActionHandle | null>;
  onActionStateChange?: (state: StageGrantActionState | null) => void;
  onSummaryChange?: (stages: StageGrantSummary[]) => void;
}) {
  const toast = useToast();
  const target = email?.trim().toLowerCase() ?? null;
  const currentTarget = useRef(target);
  currentTarget.current = target;
  const grants = useFetch(
    signal => target ? getMemberStageGrants(target, signal) : Promise.resolve(null),
    [target],
  );

  const [draft, setDraft] = useState<Draft>({});
  const [draftEmail, setDraftEmail] = useState<string | null>(null);
  const [operations, setOperations] = useState<Record<string, StageOperation>>(readStageOperations);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(!collapsible);
  const inFlight = useRef(false);

  const operation = !disabled && target ? operations[target] : undefined;
  useEffect(() => { storeStageOperations(operations); }, [operations]);
  useEffect(() => { setExpanded(!collapsible); }, [target, collapsible]);
  const loaded = !!target && grants.data?.email === target && !grants.loading && !grants.error;
  const existing = loaded ? grants.data!.grants : [];
  const existingMap = grantMap(existing);
  // 저장 중에도 draft를 그대로 쓴다 — disabled 때 existingMap으로 바꾸면 회수한 Stage가 잠깐 보유 열로 되돌아가 보인다.
  const selected = draftEmail === target ? draft : {};
  // 전송 목록 UX: 보유 패널은 "저장하면 갖게 될 Stage"(현재 선택)만 담는다.
  // 저장된 Stage를 빼면 추가 카드로 돌아가 '회수 예정' 표시가 붙고, 다시 넣으면 회수가 취소된다.
  // 넣기/빼기 순서 기록 — 최근에 움직인 Stage가 각 열의 맨 위에 쌓인다.
  const moveSeqRef = useRef<Partial<Record<AppStage, number>>>({});
  const moveCounterRef = useRef(0);
  // 최근에 넣거나 뺀 Stage가 각 열의 맨 위에 쌓이도록: 저장 전 변경분은 이동 순서 역순, 나머지는 Stage 순서.
  const bySeqThenOrder = (a: AppStage, b: AppStage) => {
    const pendingA = existingMap[a] !== selected[a];
    const pendingB = existingMap[b] !== selected[b];
    if (pendingA !== pendingB) return pendingA ? -1 : 1;
    if (pendingA) return (moveSeqRef.current[b] ?? 0) - (moveSeqRef.current[a] ?? 0);
    return APP_STAGES.indexOf(a) - APP_STAGES.indexOf(b);
  };
  const heldStages = APP_STAGES.filter(stage => !!selected[stage]).sort(bySeqThenOrder);
  const availableStages = APP_STAGES.filter(stage => !selected[stage]).sort(bySeqThenOrder);
  const changes = !disabled && draftEmail === target ? APP_STAGES.flatMap(stage =>
    existingMap[stage] === selected[stage]
      ? []
      : [{ stage, level: selected[stage] ?? null } satisfies StageGrantChange],
  ) : [];
  // Stage 원장 저장과 제공 계층 후속 동기화는 별개다. sync job을 추적하는 동안에도
  // 다음 Stage 변경을 편집할 수 있어야 하며, 응답이 불명인 저장 요청만 잠근다.
  // 남아 있는 operation = 재시도가 필요한 저장(응답 불명 또는 BigQuery 반영 실패). 성공하면 즉시 지운다.
  const pendingWrite = !!operation;
  const editable = loaded && draftEmail === target && !pendingWrite && !busy && !disabled;
  const applying = target !== null && busy === target;
  const summarySelected = draftEmail === target ? selected : existingMap;
  const summary = APP_STAGES.flatMap(stage => {
    const current = existingMap[stage];
    const next = summarySelected[stage];
    if (!current && !next) return [];
    return [{
      stage,
      level: (next ?? current)!,
      changeKind: !current ? 'grant' : !next ? 'revoke' : current !== next ? 'update' : 'current',
    } satisfies StageGrantSummary];
  });
  const summaryKey = JSON.stringify(summary);

  useEffect(() => {
    if (!loaded || !grants.data || draftEmail === target) return;
    const initial = grantMap(grants.data.grants);
    setDraft(operation ? applyChanges(initial, operation.body.changes) : initial);
    setDraftEmail(target);
  }, [loaded, grants.data, target, draftEmail, operation]);

  const setStage = (stage: AppStage, level: Level | null) => {
    if (!editable) return;
    // 넣기(없던 것 → 있음) / 빼기(있던 것 → 없음)만 이동으로 본다. 역할만 바꾸는 건 자리 유지.
    if ((level === null) !== (selected[stage] === undefined)) {
      moveSeqRef.current[stage] = ++moveCounterRef.current;
    }
    setDraft(previous => {
      const next = { ...previous };
      if (level === null) delete next[stage];
      else next[stage] = level;
      return next;
    });
  };
  const setStageRef = useRef(setStage);
  setStageRef.current = setStage;

  const toggleStage = (stage: AppStage) => {
    setStage(stage, selected[stage] ? null : existingMap[stage] ?? 'viewer');
  };
  const toggleStageRef = useRef(toggleStage);
  toggleStageRef.current = toggleStage;

  const reloadRef = useRef(grants.reload);
  reloadRef.current = grants.reload;

  // 하단 액션바의 단일 '변경 적용'이 호출한다 — 게임 변경보다 먼저 Stage 변경을 저장한다.
  // 확인창은 하단 바가 게임 변경과 함께 다루므로 여기서는 띄우지 않는다.
  const submit = async (): Promise<boolean> => {
    if (!target || disabled || inFlight.current) return false;
    if (!operation && (!editable || !changes.length)) return true;

    const command = changes.length ? {
      id: crypto.randomUUID(),
      body: { email: target, changes },
    } : operation!;
    inFlight.current = true;
    onBusyChange?.(true);
    setBusy(target);
    setOperations(previous => ({ ...previous, [target]: command }));
    setErrors(previous => ({ ...previous, [target]: '' }));

    try {
      const result = await changeStageGrants(command.body, command.id);
      if (
        result.state !== 'synced'
        || result.email !== target
        || JSON.stringify(result.changes) !== JSON.stringify(command.body.changes)
      ) {
        throw new Error('반영 결과를 확인하지 못했습니다. 같은 요청으로 재시도하세요.');
      }
      // 저장되면 이 이메일 기준으로 다시 조회한다. 제공 계층 동기화 상태는 따로 추적·표시하지 않는다.
      if (currentTarget.current === target) {
        grants.mutate(previous => {
          if (!previous || previous.email !== target) return previous;
          const next = applyChanges(grantMap(previous.grants), command.body.changes);
          const nextGrants = APP_STAGES.flatMap(stage => next[stage]
            ? [{ stage, level: next[stage]! } satisfies StageGrant]
            : []);
          return { ...previous, stages: nextGrants.map(grant => grant.stage), grants: nextGrants };
        });
        grants.reload();
        onSaved?.();
      }
      setOperations(previous => {
        const next = { ...previous };
        // BigQuery 반영 실패만 재시도 대상으로 남긴다.
        if (result.bq_sync_status === 'failed') next[target] = { ...command, bqSyncFailed: true };
        else delete next[target];
        return next;
      });
      // BigQuery dataset ACL은 Stage 권한을 따라간다. 반영 실패는 저장 성공과 구분해 보여준다.
      if (result.bq_sync_status === 'failed') {
        throw new Error(
          `Stage 권한은 저장됐지만 BigQuery 권한 반영이 실패했습니다. 같은 요청으로 재시도하세요. (${result.bq_sync_error ?? '원인 미상'})`,
        );
      }
      const bqNote = result.bq_sync_status === 'done'
        ? ` · BigQuery ${result.prefixes?.length ?? 0}개 게임 반영`
        : '';
      toast.success(`${target} · Stage 권한을 저장했어요${bqNote}`);
      return true;
    } catch (error) {
      if (error instanceof ApiHttpError && [400, 401, 403, 404, 409, 422].includes(error.status ?? 0)) {
        setOperations(previous => {
          const next = { ...previous };
          delete next[target];
          return next;
        });
        if (error.status === 409 && currentTarget.current === target) {
          setDraftEmail(null);
          grants.reload();
        }
      }
      setErrors(previous => ({ ...previous, [target]: getErrorMessage(error) }));
      return false;
    } finally {
      inFlight.current = false;
      setBusy(null);
      onBusyChange?.(false);
    }
  };

  const request = async (reason: string): Promise<boolean> => {
    if (!target || disabled || inFlight.current) return false;
    if (!editable || !changes.length) return true;
    inFlight.current = true;
    onBusyChange?.(true);
    setBusy(target);
    setErrors(previous => ({ ...previous, [target]: '' }));
    try {
      await createStageGrantRequest({ email: target, changes, reason });
      // 실제 권한은 바뀌지 않았다 — 저장된 상태 기준으로 선택을 되돌린다.
      if (currentTarget.current === target) setDraftEmail(null);
      return true;
    } catch (error) {
      setErrors(previous => ({ ...previous, [target]: getErrorMessage(error) }));
      return false;
    } finally {
      inFlight.current = false;
      setBusy(null);
      onBusyChange?.(false);
    }
  };
  const requestRef = useRef(request);
  requestRef.current = request;

  const footerButtonLabel = applying
    ? '적용 중...'
    : operation?.bqSyncFailed
      ? 'BigQuery 반영 재시도'
      : operation
        ? '동일 요청 재시도'
        : '변경 적용';
  const footerDisabled = disabled || !!busy || (!operation && (!editable || !changes.length));
  const submitRef = useRef(submit);
  submitRef.current = submit;

  useEffect(() => {
    if (!actionRef) return;
    actionRef.current = {
      submit: () => submitRef.current(),
      request: reason => requestRef.current(reason),
      setStage: (stage, level) => {
        setExpanded(true);
        setStageRef.current(stage, level);
      },
      toggleStage: stage => {
        setExpanded(true);
        toggleStageRef.current(stage);
      },
      reload: () => reloadRef.current(),
    };
    return () => { actionRef.current = null; };
  }, [actionRef]);

  useEffect(() => {
    onSummaryChange?.(summary);
  }, [onSummaryChange, summaryKey]);

  useEffect(() => () => onSummaryChange?.([]), [onSummaryChange]);

  const panelState: StageGrantActionState = {
    loaded,
    count: availableStages.length,
    editable,
    warning: changes.length ? existingGrantWarning : undefined,
    loadError: !!grants.error,
    writeError: target ? errors[target] : undefined,
    footerSummary: changes.length
      ? `Stage ${changes.length}개 변경 예정`
      : operation ? '저장 재시도 필요' : '변경 없음',
    buttonLabel: footerButtonLabel,
    buttonDisabled: footerDisabled,
    // 일반 변경은 하단 액션바의 단일 버튼이 적용한다. 카드 안 버튼은 저장 후 재시도·상태 확인이 필요할 때만.
    // 적용 중에는 하단 액션바가 진행 상태를 보이므로 카드 안 버튼은 띄우지 않는다. 재시도가 필요할 때만.
    showRetry: !!operation && !applying,
    heldStages,
    availableStages,
    changeCount: changes.length,
    applying,
    disabled: footerDisabled,
    summary: changes.length
      ? `Stage ${changes.length}개 변경 예정`
      : operation ? '저장 재시도 필요' : '변경 없음',
  };
  const panelStateKey = JSON.stringify(panelState);
  useEffect(() => {
    onActionStateChange?.(panelState);
  }, [onActionStateChange, panelStateKey]);
  useEffect(() => () => onActionStateChange?.(null), [onActionStateChange]);

  return <StageGrantsPanel
    regionLabel="Stage별 권한 설정"
    detailsId="stage-grants-details-add"
    headless
    title="권한 추가"
    selected={selected}
    visibleStages={availableStages}
    changeKinds={Object.fromEntries(summary.map(item => [item.stage, item.changeKind]))}
    state={panelState}
    emptyMessage="추가할 Stage가 없습니다. 선택한 Stage는 보유 권한에서 확인·해제할 수 있습니다."
    lockedMessage={lockedMessage}
    onToggleStage={toggleStage}
    onSetStage={setStage}
    onApply={() => { void submit(); }}
    onRetry={grants.reload}
  />;
}
